import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { BaseEditCopyService } from 'tms-lib';
import {
  AddressLabel,
  CustomerAgentSupplier,
  PurchaseOrderSupplier,
  ShipperSupplier,
  Supplier,
  SupplierDetails,
  SupplierKeyAndName,
  SupplierKeyNameAndAddress,
  SupplierPurchaseInvoiceHistory,
  SupplierPurchaseOrderHistory,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
export class SupplierService extends BaseEditCopyService<Supplier, SupplierDetails> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('Supplier', $localize`Supplier`);
  }

  getSupplierForPurchaseOrder(id: string): Observable<PurchaseOrderSupplier> {
    return this.httpService.get<PurchaseOrderSupplier>(`${this.apiUrl}/PurchaseOrder/${id}`);
  }

  getSupplierForCustomerAgent(id: string): Observable<CustomerAgentSupplier> {
    return this.httpService.get<CustomerAgentSupplier>(`${this.apiUrl}/CustomerAgent/${id}`);
  }

  /**
   * The shipper's service, account and charge for a shipment. With a shipping service the account is the
   * one matching it (consolidated or not); without, the shipper's default service decides.
   */
  getSupplierForShipping(
    id: string,
    sellingCompanyId: string,
    warehouseId: string,
    shippingServiceId?: string,
  ): Observable<ShipperSupplier> {
    const shippingService = shippingServiceId ? `&shippingServiceId=${shippingServiceId}` : '';
    return this.httpService.get<ShipperSupplier>(
      `${this.apiUrl}/Shipper?id=${id}&sellingCompanyId=${sellingCompanyId}&warehouseId=${warehouseId}${shippingService}`,
    );
  }

  getSupplierKeyAndName(id: string): Observable<SupplierKeyAndName> {
    return this.httpService.get<SupplierKeyAndName>(`${this.apiUrl}/GetKeyAndName/${id}`);
  }

  getSupplierKeyNameAndAddress(id: string): Observable<SupplierKeyNameAndAddress> {
    return this.httpService.get<SupplierKeyNameAndAddress>(`${this.apiUrl}/GetKeyNameAndAddress/${id}`);
  }

  checkKey(key: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckKey?key=${key}`);
  }

  getSupplierPurchaseOrderHistory(id: string, search: string): Observable<SupplierPurchaseOrderHistory[]> {
    return this.httpService.get<SupplierPurchaseOrderHistory[]>(
      `${this.apiUrl}/SupplierPurchaseOrderHistory?id=${id}&search=${search}`,
    );
  }

  getSupplierPurchaseInvoiceHistory(id: string, search: string): Observable<SupplierPurchaseInvoiceHistory[]> {
    return this.httpService.get<SupplierPurchaseInvoiceHistory[]>(
      `${this.apiUrl}/SupplierPurchaseInvoiceHistory?id=${id}&search=${search}`,
    );
  }

  getSupplierAddress(id: string): Observable<AddressLabel> {
    return this.httpService.get<AddressLabel>(`${this.apiUrl}/GetSupplierAddress?id=${id}`);
  }
}
